import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Play, Music } from "lucide-react";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/ui/animated-section";
import { ForgedLine } from "@/components/ui/forged-line";

export function FeaturedArtistsGrid() {
  const { data: artists } = useQuery({
    queryKey: ["featured-artists-grid"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, slug, genre, logo_url")
        .not("slug", "is", null)
        .order("name")
        .limit(4);
      if (error) throw error;
      return data;
    },
  });

  if (!artists || artists.length === 0) return null;

  return (
    <section className="py-20 md:py-28 bg-card">
      <div className="container mx-auto px-4">
        <AnimatedSection animation="fade-up" as="div">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground uppercase tracking-[0.08em] mb-2">
            Featured Artists
          </h2>
          <ForgedLine variant="horizontal" className="mb-4" />
          <p className="text-muted-foreground text-lg max-w-2xl mb-16">
            Meet the artists carrying the mission forward.
          </p>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl">
          {artists.map((artist, i) => (
            <AnimatedSection key={artist.id} animation="fade-up" as="div" delay={i * 80}>
              <Link
                to={`/artists/${artist.slug}`}
                className="group block rounded-[4px] overflow-hidden border border-border bg-background transition-all duration-300 hover:border-accent hover:shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
              >
                {/* Image */}
                <div className="aspect-square overflow-hidden relative bg-muted">
                  {artist.logo_url ? (
                    <img
                      src={artist.logo_url}
                      alt={artist.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <Music className="h-16 w-16 text-muted-foreground/20" />
                    </div>
                  )}
                  {/* Play icon overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-background/0 group-hover:bg-background/30 transition-all duration-300">
                    <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300">
                      <Play className="h-5 w-5 text-accent-foreground ml-0.5" />
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-foreground text-sm mb-1 truncate">
                    {artist.name}
                  </h3>
                  {artist.genre && (
                    <span className="text-xs text-accent uppercase tracking-[0.08em]">
                      {artist.genre}
                    </span>
                  )}
                </div>
              </Link>
            </AnimatedSection>
          ))}
        </div>

        {artists.length >= 4 && (
          <AnimatedSection animation="fade-up" as="div" delay={400} className="mt-10">
            <Button variant="outline" asChild>
              <Link to="/artists">View All Artists</Link>
            </Button>
          </AnimatedSection>
        )}
      </div>
    </section>
  );
}
